import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { api, type InningWithPitches } from '../services/api';
import { toast } from './shared/Toast';
import './shared/ConfirmDialog';

@customElement('inning-tracker')
export class InningTracker extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  gameId = '';

  @property()
  homeTeamName = 'Home';

  @property()
  awayTeamName = 'Away';

  @property({ type: Boolean })
  compact = false;

  @property({ type: Boolean })
  viewOnly = false;

  @state()
  private currentInning: InningWithPitches | null = null;

  @state()
  private innings: InningWithPitches[] = [];

  @state()
  private loading = false;

  @state()
  private error = '';

  // "Remove this inning" affordance — only surfaced when the current inning
  // is the latest, empty, and has a previous fall-back. Soft-deletes via
  // DELETE /api/games/{id}/innings/{inningId}; admin can restore.
  @state()
  private removeConfirmOpen = false;

  @state()
  private removing = false;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadInnings();
  }

  async updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('gameId') && this.gameId) {
      await this.loadInnings();
    }
  }

  private async loadInnings() {
    if (!this.gameId) return;

    this.loading = true;
    this.error = '';
    try {
      this.innings = (await api.getGameInnings(this.gameId)) || [];
      this.currentInning = this.innings[this.innings.length - 1] || null;
      if (this.currentInning) {
        this.dispatchInningChanged(this.currentInning);
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load innings';
    } finally {
      this.loading = false;
    }
  }

  private dispatchInningChanged(inning: InningWithPitches) {
    this.dispatchEvent(
      new CustomEvent('inning-changed', {
        detail: inning,
        bubbles: true,
        composed: true,
      })
    );
  }

  private selectInning(inning: InningWithPitches) {
    this.currentInning = inning;
    this.dispatchInningChanged(inning);
  }

  private get currentInningIndex(): number {
    if (!this.currentInning) return -1;
    return this.innings.findIndex((i) => i.id === this.currentInning!.id);
  }

  private get hasPreviousInning(): boolean {
    return this.currentInningIndex > 0;
  }

  private get isOnLastInning(): boolean {
    return this.currentInningIndex === this.innings.length - 1;
  }

  private goToPreviousInning() {
    const idx = this.currentInningIndex;
    if (idx > 0) {
      this.selectInning(this.innings[idx - 1]);
    }
  }

  private async goToNextInning() {
    const idx = this.currentInningIndex;
    if (idx < this.innings.length - 1) {
      // Navigate to next existing inning
      this.selectInning(this.innings[idx + 1]);
    } else {
      // On last inning — create new
      await this.advanceInning();
    }
  }

  private async advanceInning() {
    if (!this.gameId) return;

    this.loading = true;
    this.error = '';
    try {
      const inning = await api.advanceInning(this.gameId);
      const newInning: InningWithPitches = { ...inning, pitches: [] };
      this.innings = [...(this.innings || []), newInning];
      this.currentInning = newInning;
      this.dispatchInningChanged(newInning);
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to advance inning';
    } finally {
      this.loading = false;
    }
  }

  private async endCurrentInning() {
    if (!this.gameId || !this.currentInning) return;

    this.loading = true;
    this.error = '';
    try {
      await api.endInning(this.gameId, this.currentInning.id);
      await this.loadInnings();
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to end inning';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Whether to render the "Remove this inning" trash button. All four must
   * hold simultaneously — the button is the recovery path for an accidental
   * "Next inning" tap and we never want it to delete pitch data.
   */
  private get canRemoveCurrentInning(): boolean {
    if (this.viewOnly) return false;
    if (!this.currentInning) return false;
    if (this.currentInning.pitches.length !== 0) return false;
    if (!this.isOnLastInning) return false;
    if (!this.hasPreviousInning) return false;
    return true;
  }

  private get previousInningLabel(): string {
    const idx = this.currentInningIndex;
    if (idx <= 0) return '';
    return this.formatInningLong(this.innings[idx - 1]);
  }

  private openRemoveConfirm() {
    if (!this.canRemoveCurrentInning) return;
    this.removeConfirmOpen = true;
  }

  private cancelRemoveConfirm() {
    if (this.removing) return;
    this.removeConfirmOpen = false;
  }

  private async confirmRemoveInning() {
    if (!this.gameId || !this.currentInning) return;
    const inningId = this.currentInning.id;

    this.removing = true;
    this.error = '';
    try {
      await api.deleteInning(this.gameId, inningId);
      this.removeConfirmOpen = false;
      // Reload — the deleted inning falls out of the list and the prior
      // inning becomes current automatically via loadInnings().
      await this.loadInnings();
      toast.success('Inning removed');
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to remove inning';
      this.removeConfirmOpen = false;
    } finally {
      this.removing = false;
    }
  }

  private renderRemoveDialog() {
    if (!this.removeConfirmOpen) return nothing;
    const prevLabel = this.previousInningLabel || 'the previous inning';
    return html`
      <confirm-dialog
        .open=${this.removeConfirmOpen}
        title="Remove this half-inning?"
        .message=${`You'll go back to ${prevLabel}. The empty inning will be removed — an admin can restore it if needed.`}
        confirmText="Remove inning"
        cancelText="Cancel"
        .destructive=${true}
        .loading=${this.removing}
        @confirm=${this.confirmRemoveInning}
        @cancel=${this.cancelRemoveConfirm}
      ></confirm-dialog>
    `;
  }

  /**
   * Kebab menu attached to the current-inning badge. The menu only renders
   * a "Remove inning" item when canRemoveCurrentInning is true; otherwise
   * the kebab itself is hidden so it doesn't suggest an empty/disabled menu.
   *
   * The kebab is visually adjacent to the inning badge so it's obvious
   * which inning the action targets (vs the previous standalone trash
   * icon, which sat in the controls row and ambiguously read as "remove
   * the inning that was last navigated to" or "remove the inning the
   * scroller is showing").
   */
  private renderInningKebab() {
    if (!this.canRemoveCurrentInning) return nothing;
    const inningLabel = this.formatInningLong(this.currentInning);
    return html`
      <div class="dropdown dropdown-end">
        <button
          tabindex="0"
          class="btn btn-ghost btn-xs btn-square text-base-content/50 hover:text-base-content"
          aria-label="Actions for ${inningLabel}"
          title="Inning actions"
          ?disabled=${this.loading || this.removing}
        >
          ${this.removing
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : html`
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <circle cx="12" cy="5" r="0.8" fill="currentColor" />
                  <circle cx="12" cy="12" r="0.8" fill="currentColor" />
                  <circle cx="12" cy="19" r="0.8" fill="currentColor" />
                </svg>
              `}
        </button>
        <ul tabindex="0" class="dropdown-content menu menu-sm bg-base-100 rounded-box shadow-lg border border-base-200 z-50 mt-1 w-52 p-1">
          <li>
            <button
              type="button"
              class="text-error hover:bg-error/10 gap-2 py-2"
              @click=${this.openRemoveConfirm}
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
              </svg>
              <span>Remove ${inningLabel}</span>
            </button>
          </li>
        </ul>
      </div>
    `;
  }

  private get battingTeam(): string {
    if (!this.currentInning) return this.awayTeamName;
    return this.currentInning.half === 'top' ? this.awayTeamName : this.homeTeamName;
  }

  private formatInningLabel(inning: InningWithPitches): string {
    const half = inning.half === 'top' ? '▲' : '▼';
    return `${inning.inning_number}${half}`;
  }

  private formatInningLong(inning: InningWithPitches | null): string {
    if (!inning) return 'Top 1st';
    const half = inning.half === 'top' ? 'Top' : 'Bot';
    const n = inning.inning_number;
    const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return `${half} ${n}${suffix}`;
  }

  render() {
    if (this.compact) {
      return this.renderCompact();
    }

    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body py-4">
          ${this.error
            ? html`
                <div class="alert alert-error alert-sm mb-2">
                  <span>${this.error}</span>
                </div>
              `
            : nothing}

          <div class="flex items-center justify-between gap-4">
            <!-- Current inning display -->
            <div class="text-center min-w-[80px] relative">
              <div class="text-4xl font-bold tabular-nums">
                ${this.currentInning?.inning_number || 1}
              </div>
              <div class="text-sm text-base-content/70 flex items-center justify-center gap-1">
                ${!this.currentInning || this.currentInning.half === 'top'
                  ? html`<span class="text-lg">▲</span> Top`
                  : html`<span class="text-lg">▼</span> Bottom`}
              </div>
              <div class="absolute top-0 right-0 -mr-1">
                ${this.renderInningKebab()}
              </div>
            </div>

            <!-- Team indicator -->
            <div class="text-center flex-1">
              <div class="text-lg font-medium">${this.battingTeam}</div>
              <div class="text-xs text-base-content/60">at bat</div>
            </div>

            <!-- Controls -->
            <div class="flex gap-2 items-center">
              <button
                class="btn btn-ghost btn-sm"
                @click=${this.goToPreviousInning}
                ?disabled=${this.loading || !this.hasPreviousInning}
                title="Previous half-inning"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              ${this.viewOnly ? nothing : html`
                <button
                  class="btn btn-primary btn-sm"
                  @click=${this.goToNextInning}
                  ?disabled=${this.loading}
                  title=${this.isOnLastInning ? 'Advance to next half-inning' : 'Go to next half-inning'}
                >
                  ${this.loading
                    ? html`<span class="loading loading-spinner loading-xs"></span>`
                    : html`
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      `}
                  ${this.isOnLastInning ? 'Next' : ''}
                </button>
              `}
            </div>
          </div>

          <!-- Clickable inning pills -->
          ${this.innings.length > 0
            ? html`
                <div class="flex gap-1 mt-3 overflow-x-auto pb-1">
                  ${this.innings.map(
                    (inning) => html`
                      <button
                        class="badge ${inning.id === this.currentInning?.id
                          ? 'badge-primary'
                          : 'badge-ghost'} badge-sm whitespace-nowrap cursor-pointer hover:badge-primary/70 transition-colors"
                        @click=${() => this.selectInning(inning)}
                      >
                        ${this.formatInningLabel(inning)}
                      </button>
                    `
                  )}
                </div>
              `
            : nothing}
        </div>
        ${this.renderRemoveDialog()}
      </div>
    `;
  }

  private renderCompact() {
    return html`
      <div class="card bg-base-100 shadow-sm overflow-hidden animate-fade-in">
        <div class="card-body p-0">
          <!-- Header with title -->
          <div class="flex items-center justify-between px-3 pt-2 pb-1.5 sm:px-4 sm:pt-3 sm:pb-2">
            <div class="flex items-center gap-1.5 sm:gap-2">
              <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 class="text-xs sm:text-sm font-semibold text-base-content">Current Inning</h3>
                <p class="text-[10px] sm:text-xs text-base-content/60">${this.battingTeam} batting</p>
              </div>
            </div>
            
            <!-- Inning display badge - keyed to re-animate on change.
                 Kebab dropdown lives inside the badge so it visually
                 belongs to the inning it acts on. -->
            ${keyed(
              this.currentInning?.id ?? 'initial',
              html`
                <div class="inning-badge">
                  <span class="inning-badge-half ${this.currentInning?.half === 'top' ? 'top' : 'bottom'}">
                    ${!this.currentInning || this.currentInning.half === 'top' ? '▲' : '▼'}
                  </span>
                  <span class="inning-badge-number">${this.currentInning?.inning_number || 1}</span>
                  ${this.renderInningKebab()}
                </div>
              `
            )}
          </div>

          <!-- Navigation controls -->
          <div class="flex items-center gap-1.5 px-3 pb-2 sm:gap-2 sm:px-4 sm:pb-3">
            <!-- Back button -->
            <button
              class="btn btn-ghost btn-sm btn-square transition-all duration-200 hover:bg-base-200"
              @click=${this.goToPreviousInning}
              ?disabled=${this.loading || !this.hasPreviousInning}
              title="Previous half-inning"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <!-- Scrollable inning pills -->
            <div class="flex-1 flex gap-1.5 overflow-x-auto py-1 px-1 scroll-smooth inning-pills">
              ${this.innings.length > 0
                ? this.innings.map(
                    (inning) => html`
                      <button
                        class="inning-pill ${inning.id === this.currentInning?.id ? 'active' : ''}"
                        @click=${() => this.selectInning(inning)}
                      >
                        ${this.formatInningLabel(inning)}
                      </button>
                    `
                  )
                : html`
                    <button class="inning-pill active">1▲</button>
                  `}
            </div>

            <!-- Forward / Advance button -->
            ${this.viewOnly ? nothing : html`
              <button
                class="btn ${this.isOnLastInning ? 'btn-primary' : 'btn-ghost'} btn-sm gap-1 transition-all duration-200"
                @click=${this.goToNextInning}
                ?disabled=${this.loading}
                title=${this.isOnLastInning ? 'Advance to next half-inning' : 'Go to next half-inning'}
              >
                ${this.loading
                  ? html`<span class="loading loading-spinner loading-xs"></span>`
                  : this.isOnLastInning
                    ? html`
                        <span class="text-xs">Next</span>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      `
                    : html`
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      `}
              </button>
            `}
          </div>
        </div>
        ${this.renderRemoveDialog()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'inning-tracker': InningTracker;
  }
}
